import { useMemo } from "react";
import { EmptyState } from "@/components/EmptyState";
import { Loaded } from "@/components/Loading";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { usePeople } from "@/lib/hooks/usePeople";
import { CALLABLES } from "@/services/callables";
import { formatDate, formatDateTime } from "@/lib/format";
import { lessonKind, lessonKindKey } from "@/features/sessions/beats";
import { driesAcrossLessons } from "@/features/sessions/dries";
import type { Group, GroupDries } from "@/types";

/**
 * Lessons run with this group.
 *
 * The list, and — for the teacher reading it — their own off-book line per
 * student. What happened inside a lesson belongs to the teacher who taught it:
 * the backend answers the dries with that teacher's lessons and no colleague's,
 * so an owner who never taught the group sees the list and nothing under it.
 */

interface SessionSummary {
  id: string;
  teacherUid: string;
  mode: "online" | "offline";
  state: string;
  startedAt: string;
  endedAt?: string;
  scenarioId?: string;
  register: { uid: string; present?: boolean }[];
}

const LIVE_STATES = new Set(["lobby", "casting", "scene", "debrief"]);

export function HistoryTab({ group }: { group: Group }) {
  const t = useT();
  const { locale } = useI18n();
  const { people } = usePeople();
  const sessions = useCallable<{ sessions: SessionSummary[] }, { groupId: string }>(
    CALLABLES.listSessions,
    { groupId: group.id }
  );

  const nameOf = (uid: string) =>
    people.find((person) => person.uid === uid)?.displayName ?? uid;

  return (
    <Loaded state={sessions}>
      {(loaded) =>
        loaded.sessions.length === 0 ? (
          <EmptyState titleKey="group.history.empty" />
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <caption className="sr-only">{t("group.tab.history")}</caption>
              <thead>
                <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("group.history.column.date")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("group.history.column.teacher")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("group.history.column.mode")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("group.history.column.attendance")}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.sessions.map((session) => (
                  <tr key={session.id} className="border-b border-line last:border-0">
                    <td className="h-10 px-3 py-2.5 text-ink">
                      {formatDateTime(locale, session.startedAt)}
                      {/*
                        The one product colour left in Backstage: green here
                        means what it means on a device in the room — this is
                        happening now. Elsewhere on this screen green would be
                        an interface "fine", which a live lesson is not.
                      */}
                      {LIVE_STATES.has(session.state) ? (
                        <span className="ml-2 text-cue-green">{t("group.history.live")}</span>
                      ) : null}
                      {lessonKind(session) ? (
                        <span className="ml-2 text-ink-muted">{t(lessonKindKey(lessonKind(session)!))}</span>
                      ) : null}
                    </td>
                    <td className="h-10 px-3 py-2.5 text-ink-muted">{nameOf(session.teacherUid)}</td>
                    <td className="h-10 px-3 py-2.5 text-ink-muted">
                      {t(`institution.mode.${session.mode}`)}
                    </td>
                    <td className="h-10 px-3 py-2.5 text-ink-muted">{session.register.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <OffBook group={group} nameOf={nameOf} />
          </>
        )
      }
    </Loaded>
  );
}

/**
 * Off-book progress: one line per student, one number per scripted run they
 * were in, oldest lesson first.
 *
 * Counts, read left to right, and nothing else — no score, no trend line.
 * Shown only once there is a scripted run to count; a group that has never
 * rehearsed a script has no section here rather than an empty one.
 */
function OffBook({ group, nameOf }: { group: Group; nameOf: (uid: string) => string }) {
  const t = useT();
  const { locale } = useI18n();
  const dries = useCallable<GroupDries, { groupId: string }>(CALLABLES.getGroupDries, {
    groupId: group.id,
  });

  const lines = useMemo(() => {
    const byUid = driesAcrossLessons(dries.data?.sessions ?? []);
    // The roster's order, then anybody since removed from it.
    const order = [
      ...group.roster.map((entry) => entry.uid).filter((uid) => byUid.has(uid)),
      ...[...byUid.keys()].filter((uid) => !group.roster.some((entry) => entry.uid === uid)),
    ];
    return order.map((uid) => ({ uid, entries: byUid.get(uid) ?? [] }));
  }, [dries.data, group.roster]);

  if (dries.loading || dries.failed || lines.length === 0) return null;

  return (
    <section className="mt-8 max-w-3xl" aria-label={t("group.history.dries.title")}>
      <h2 className="section-title">{t("group.history.dries.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("group.history.dries.intro")}</p>
      <ul className="mt-3 flex flex-col gap-1.5 text-sm">
        {lines.map((line) => (
          <li key={line.uid} className="flex flex-wrap items-baseline gap-x-3">
            <span className="w-40 shrink-0 text-ink">{nameOf(line.uid)}</span>
            <span className="flex flex-wrap gap-x-2 text-ink-soft">
              {line.entries.map((entry, index) => (
                <span
                  key={`${entry.sessionId}-${entry.runId}`}
                  title={t("group.history.dries.entry", {
                    date: formatDate(locale, entry.startedAt),
                    count: entry.dries,
                    beats: entry.beats,
                  })}
                >
                  {index > 0 ? <span className="mr-2 text-ink-faint">·</span> : null}
                  {entry.dries}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
